import * as React from "react";
import {useEffect, useRef, useState} from "react";
import {buttonClasses, Grow, MenuList, Paper, Popper} from "@mui/material";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import {AuthButtonVO} from "@/service/menu.s";

export interface MoreButtonProps {
    title: string;
    items: AuthButtonVO[];
    handleSelect?: (item: AuthButtonVO) => void;
    startIcon?: React.ReactNode;
}

const MoreButton: React.FC<MoreButtonProps> = ({
                                                   title,
                                                   items,
                                                   handleSelect,
                                                   startIcon,
                                               }) => {
    const [open, setOpen] = useState<boolean>(false);
    const anchorRef = useRef<HTMLButtonElement>(null);
    const prevOpen = useRef(open);

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (
        event: Event | React.SyntheticEvent,
        item: AuthButtonVO,
        selected = true,
    ) => {
        if (
            anchorRef.current &&
            anchorRef.current.contains(event.target as HTMLElement)
        ) {
            return;
        }
        if (handleSelect && selected) {
            handleSelect(item);
        }
        setOpen(false);
    };

    function handleListKeyDown(event: React.KeyboardEvent) {
        if (event.key === "Tab") {
            event.preventDefault();
            setOpen(false);
        } else if (event.key === "Escape") {
            setOpen(false);
        }
    }

    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current!.focus();
        }
        prevOpen.current = open;
    }, [open]);

    const MenuPopper = () => {
        return (
            <Popper
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                placement="bottom-start"
                sx={{
                    zIndex: 1300,
                    borderRadius: "8px",
                    border: "1px solid var(--template-palette-divider)",
                }}
                transition
                disablePortal
            >
                {({TransitionProps, placement}) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin:
                                placement === "bottom-start" ? "left top" : "left bottom",
                        }}
                    >
                        <Paper>
                            <ClickAwayListener
                                onClickAway={(event) => handleClose(event, {} as AuthButtonVO, false)}
                            >
                                <MenuList
                                    autoFocusItem={open}
                                    dense={true}
                                    disablePadding={true}
                                    id="composition-menu"
                                    aria-labelledby="composition-button"
                                    onKeyDown={handleListKeyDown}
                                >
                                    {items?.map((item: AuthButtonVO, index: number) => (
                                        <MenuItem
                                            sx={{paddingX: "4px"}}
                                            key={index}
                                            onClick={(event) => handleClose(event, {...item})}
                                        >
                                            {item.menuName}
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>)
                }
            </Popper>
        );
    };

    return (
        <>
            <Button
                variant="outlined"
                ref={anchorRef}
                id="composition-button"
                aria-controls={open ? "composition-menu" : undefined}
                aria-expanded={open ? "true" : undefined}
                aria-haspopup="true"
                onClick={handleToggle}
                sx={(theme) => ({
                    [`& .${buttonClasses.startIcon} > *:nth-of-type(1)`]: {
                        fontSize: "1.4rem",
                        width: theme.typography.pxToRem(24),
                        height: theme.typography.pxToRem(24),
                    },
                })}
                startIcon={startIcon}
            >
                {title}
            </Button>
            <MenuPopper/>
        </>
    );
};

export default MoreButton;
